import { useState, useEffect } from "react";
import { FaCloudUploadAlt, FaCheckCircle } from "react-icons/fa";
import SecureStatementNotice from "../SecureStatementNotice";

export default function FileUploadComponent({route_page = "orders"}) {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("replace"); 
  const [consentSource, setConsentSource] = useState("in-store");


  useEffect(() => {
  if (success) {
      const timer = setTimeout(() => {
        window.location.href = `/seller/${route_page}`;
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [success, route_page]);

  const handleConsentChange = (value) => {
    setConsentSource(value);
  };

  const handleFileSelect = (e) => {
    setFile(e.target.files[0]);
    setError(null);
    setSuccess(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setFile(e.dataTransfer.files[0]);
    setError(null);
    setSuccess(false);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a CSV or Excel file first.");
      return;
    }

    setLoading(true);
    setProgress(20);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mode", mode);
      formData.append("consent_source", consentSource);
      const token = localStorage.getItem("access_token");

      const response = await fetch(baseUrl + "/upload-orders/", {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: formData,
      });

      setProgress(70);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Upload failed. Please check your file format.");
      }

      setProgress(100);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Unexpected error occurred");
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="p-2 text-center"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <h5 className="fw-bold mb-3 text-dark text-left">📦 Upload Orders</h5>

      {success ? (
        <div className="text-success mb-3">
          <FaCheckCircle size={24} className="me-2" />
          File uploaded successfully! Reloading...
        </div>
      ) : (
        <>
          <div className="upload-box rounded-3 p-4 mb-3 bg-white font9" 
            style={{
              border: "3px dashed #0070ff",
            }}>
            <div className="text-center text-muted">
              <FaCloudUploadAlt size={50} color="#0070ff" className="mb-2" />
              <p className="mb-1">Drag and drop excel or csv file here</p>
              <p className="mb-2">
                or{" "}
                <label
                  htmlFor="fileInput"
                  className="text-primary pointer fw-bold"
                  style={{ cursor: "pointer",fontSize: "18px" }}
                >
                  browse
                </label>
              </p>
              <input
                id="fileInput"
                type="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileSelect}
                className="d-none"
              />
              {file && <p className="text-dark small mt-2">{file.name}</p>}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger py-2 small mb-3">{error}</div>
          )}

          {loading && (
            <div className="progress mb-3" style={{ height: "8px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${progress}%`, backgroundColor: "#0070ff" }}
              ></div>
            </div>
          )}

          <div className="text-start mb-3">
            <div className="row">
              <div className="col-6 pr-0">
                <div className="form-check">
                  <input
                    className="form-check-input col-4"
                    type="radio"
                    name="uploadMode"
                    id="replaceMode"
                    value="replace"
                    checked={mode === "replace"}
                    onChange={() => setMode("replace")}
                  />
                  <label className="form-check-label font7 ms-1 col-8 pl-2" htmlFor="replaceMode">
                    Replace existing records
                  </label>
                </div>
              </div>

              <div className="col-6 pr-0">
                <div className="form-check">
                  <input
                    className="form-check-input col-4"
                    type="radio"
                    name="uploadMode"
                    id="appendMode"
                    value="append"
                    checked={mode === "append"}
                    onChange={() => setMode("append")}
                  />
                  <label className="form-check-label font7 ms-1 col-8 pl-2" htmlFor="appendMode">
                    Append to existing records
                  </label>
                </div>
              </div>
            </div>
          </div>



          <button
            onClick={handleUpload}
            className="btn btn-primary col-12 rounded-3 mb-2"
            disabled={loading || !file}
          >
            {loading ? "Uploading..." : "Upload"}
          </button>

          <hr/>

          <div className="accordion mb-1 mt-3" id="consentAccordion">
              <div className="accordion-item border-0">

                  <span className="accordion-header" id="headingConsent">
                    <button
                      className="accordion-button collapsed p-1 fw-bold font8 text-muted d-flex justify-content-between align-items-center excelfile-accordion bg-white"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseConsent"
                      aria-expanded="false"
                      aria-controls="collapseConsent"
                    >
                      <span>How was consent obtained?</span>

                      <span className="badge bg-success-subtle text-success ms-2 text-capitalize">
                        {consentSource === "in-store" && "In-store"}
                        {consentSource === "whatsapp" && "WhatsApp"}
                        {consentSource === "online" && "Online"}
                        {consentSource === "woocommerce" && "WooCommerce"}
                      </span>
                    </button>
                  </span>

                  <div
                    id="collapseConsent"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingConsent"
                    data-bs-parent="#consentAccordion"
                  >
                      <div className="accordion-body text-start bg-white pt-1">

                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="consentSource"
                            id="consentInStore"
                            value="in-store"
                            checked={consentSource === "in-store"}
                            onChange={() => handleConsentChange("in-store")}
                          />
                          <label className="form-check-label font7 ms-2" htmlFor="consentInStore">
                            In-store
                          </label>
                        </div>

                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="consentSource"
                            id="consentWhatsapp"
                            value="whatsapp"
                            checked={consentSource === "whatsapp"}
                            onChange={() => handleConsentChange("whatsapp")}
                          />
                          <label className="form-check-label font7 ms-2" htmlFor="consentWhatsapp">
                            WhatsApp
                          </label>
                        </div>

                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="consentSource"
                            id="consentOnline"
                            value="online"
                            checked={consentSource === "online"}
                            onChange={() => handleConsentChange("online")}
                          />
                          <label className="form-check-label font7 ms-2" htmlFor="consentOnline">
                            Online Checkout
                          </label>
                        </div>

                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="consentSource"
                            id="consentWoo"
                            value="woocommerce"
                            checked={consentSource === "woocommerce"}
                            onChange={() => handleConsentChange("woocommerce")}
                          />
                          <label className="form-check-label font7 ms-2" htmlFor="consentWoo">
                            WooCommerce
                          </label>
                        </div>

                      </div>
                  </div>

              </div>
          </div>

          {/* ⭐ CSV/Excel Column Requirements (Accordion) */}
          <div className="accordion mb-3" id="fileGuideAccordion">
            <div className="accordion-item border-0">
              <span className="accordion-header" id="headingGuide">
                <button
                  className="accordion-button collapsed fw-bold excelfile-accordion p-1 bg-white text-muted font8"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseGuide"
                  aria-expanded="false"
                  aria-controls="collapseGuide"
                >
                  Spreadsheet Requirements
                </button>
              </span>

              <div
                id="collapseGuide"
                className="accordion-collapse collapse"
                aria-labelledby="headingGuide"
                data-bs-parent="#fileGuideAccordion"
              >
                <div
                  className="accordion-body text-left"
                  style={{
                    borderRadius: "0 4px 4px 0",
                    fontSize: "13px",
                  }}
                >
                  <h6 className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                    Required Columns
                  </h6>

                  <p className="m-0" style={{ fontSize: "12px", color: "#333" }}>
                    Your CSV or Excel file <b>must include</b> the following columns:
                  </p>

                  <ul className="mt-2 mb-3" style={{ fontSize: "12px", paddingLeft: "18px" }}>
                    <li><b>product name</b> – Name of the product purchased</li>
                    <li><b>qty</b> – Quantity bought</li>
                    <li><b>price</b> – Price or total paid</li>
                    <li><b>customer name</b> – Buyer name</li>
                    <li><b>customer email/customer phone</b> – Buyer contact</li>
                    <li><b>date</b> – Date of purchase</li>
                  </ul>

                  <h6 className="fw-bold mb-1 text-dark" style={{ fontSize: "12px" }}>
                    Optional (recommended for better targeting)
                  </h6>

                  <ul className="mt-1 mb-1" style={{ fontSize: "12px", paddingLeft: "18px" }}>
                    <li>customer address</li>
                    <li>customer city</li>
                    <li>payment method</li>
                    <li>payment status</li>
                  </ul>

                  <p className="mt-2" style={{ fontSize: "11px", color: "#666" }}>
                    Tip: Adding these optional fields helps Kadhr generate more accurate insights.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <SecureStatementNotice/>

        </>
      )}
    </div>

  );
}
